// Angular Modules
import { ENVIRONMENT_INITIALIZER, ErrorHandler, NgModule, inject } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { AppRoutingModule } from './app-routing.module';
import { ReactiveFormsModule } from '@angular/forms';
import { AppComponent } from './app.component';
import { HttpClient, HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi, withXsrfConfiguration } from '@angular/common/http';
import { FingerprintAIO } from '@awesome-cordova-plugins/fingerprint-aio/ngx';
import { LottieComponent, provideCacheableAnimationLoader, provideLottieOptions } from 'ngx-lottie';
import player from 'lottie-web';
// Feature Modules

// UI Modules
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';

// Global Services

// 3-rd Party Libs

// Shared Modules

import { LoadingService } from './common/services/loading/loading.service';
import { LoadingInterceptor } from './system/http-interceptors/loading-interceptor.interceptor';
import { XsrfInterceptor } from './system/http-interceptors/xsrf-interceptor.interceptor';
import { MEErrorService } from './common/error/me-error.service';
import { MEAPIService } from './common/services/api/me-api.service';
import { ExternalSettingsService } from './common/services/external-settings/external-settings.service';
import { ResponsiveDesignService } from './common/services/responsive-design/responsive-design.service'

import { CommonModule, CurrencyPipe, DecimalPipe, registerLocaleData, TitleCasePipe } from '@angular/common';
import { RootComponent } from './root/root.component';
import { MaterialModule } from '../material.module';
import { MESvgIconsModule } from './common/me-svg-icons.module';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { BreadcrumbComponent } from './common/components/breadcrumb/breadcrumb.component';
import { FooterComponent } from './common/components/footer/footer.component';
import { HeaderComponent } from './common/components/header/header.component';
import { LoginControlComponent } from './common/components/login-control/login-control.component';
import { SecureIconsComponent } from './common/components/secure-icons/secure-icons.component';
import { SidebarComponent } from './common/components/sidebar/sidebar.component';
import { SidebarNavComponent } from './common/components/sidebar-nav/sidebar-nav.component';
import { MeSidebarGroupComponent } from './common/components/sidebar/me-sidebar-group/me-sidebar-group.component';
import { MeSidebarActivityComponent } from './common/components/sidebar/me-sidebar-activity/me-sidebar-activity.component';
import { SpinnerComponent } from './common/components/spinner/spinner.component';
import { UserHeaderComponent } from './common/components/user-header/user-header.component';
import { UserInfoComponent } from './common/components/user-info/user-info.component';
import { LoggerModule } from "ngx-logger";

import { environment } from '../environments/environment';
import { AutocompleteGroupDirective } from './system/utility/autocomplete-group.directive';
import { TopbarNavComponent } from './common/components/topbar-nav/topbar-nav.component';
import { TopHeaderComponent } from './common/components/top-header/top-header.component';
import { TopMenuFooterComponent } from './common/components/top-menu-footer/top-menu-footer.component';
import { MEErrorHandler } from './system/core/meerror-handler';
import { UniversalComponentsModule } from './common/components/universal-components/universal-components.module';
import { SystemUnavailableComponent } from './common/components/system-unavailable/system-unavailable.component';
import { HelpDeskService } from './business/help-desk/help-desk.service';
import { BottomBarNavComponent } from './common/components/bottom-bar-nav/bottom-bar-nav.component';
import { SidebarMobileComponent } from './common/components/sidebar-mobile/sidebar-mobile.component';
import { PersonalFinanceManagementHeaderComponent } from './common/components/personal-finance-management-header/personal-finance-management-header.component';
import { PersonalFinanceManagementDialogComponent } from './common/components/personal-finance-management-header/personal-finance-management-dialog/personal-finance-management-dialog.component';
import { VoiceCommandHeaderComponent } from './common/components/voice-command-header/voice-command-header.component';
import { SignaturePadComponent } from './common/components/signature-pad/signature-pad.component';

import { IonicModule, IonicRouteStrategy } from '@ionic/angular';
import { RouteReuseStrategy } from '@angular/router';
import { register as registerSwiperElement } from 'swiper/element/bundle';
import { VoiceRecognitionComponent } from './common/components/voice-recognition/voice-recognition.component';
import { NgxChartsModule } from '@swimlane/ngx-charts';

import localeDe from '@angular/common/locales/de'
import localeDeExtra from '@angular/common/locales/extra/de';
import { VizSidebarActivityComponent } from './common/components/sidebar/viz-sidebar-activity/viz-sidebar-activity.component';
import { BaseSidebarActivityComponent } from './common/components/sidebar/base-sidebar-activity/base-sidebar-activity.component';
import { SidebarActivityDirective } from './common/components/sidebar/sidebar-activity.directive';

registerLocaleData(localeDe, 'de-DE', localeDeExtra);
registerSwiperElement(); // Register Swiper custom elements globally

@NgModule({ declarations: [
        AppComponent,
        RootComponent,
        SpinnerComponent,
        HeaderComponent,
        UserInfoComponent,
        UserHeaderComponent,
        LoginControlComponent,
        FooterComponent,
        SecureIconsComponent,
        SidebarComponent,
        SidebarMobileComponent,
        SidebarNavComponent,
        BottomBarNavComponent,
        MeSidebarGroupComponent,
        SidebarActivityDirective,
        BaseSidebarActivityComponent,
        MeSidebarActivityComponent,
        VizSidebarActivityComponent,
        BreadcrumbComponent,
        AutocompleteGroupDirective,
        TopbarNavComponent,
        TopHeaderComponent,
        TopMenuFooterComponent,
        SystemUnavailableComponent,
        PersonalFinanceManagementHeaderComponent,
        PersonalFinanceManagementDialogComponent,
        VoiceCommandHeaderComponent,
        SignaturePadComponent,
        VoiceRecognitionComponent,
    ],
    exports: [
        LottieComponent,
        BrowserModule,
        BrowserAnimationsModule,
        ReactiveFormsModule,
        CommonModule,
        TranslateModule,
        HeaderComponent,
        FooterComponent,
        TopHeaderComponent,
    ],
    bootstrap: [AppComponent], imports: [LottieComponent,
        BrowserModule,
        BrowserAnimationsModule,
        ReactiveFormsModule,
        CommonModule,
        MaterialModule,
        MESvgIconsModule,
        AppRoutingModule,
        TranslateModule.forRoot({
            loader: {
                provide: TranslateLoader,
                useFactory: HttpLoaderFactory,
                deps: [HttpClient],
            },
        }),
        LoggerModule.forRoot({
            level: environment.logger.level,
            serverLogLevel: environment.logger.serverLogLevel,
            serverLoggingUrl: environment.logger.serverLoggingUrl,
        }),
        UniversalComponentsModule,
        IonicModule.forRoot({}),
        NgxChartsModule], providers: [
        { provide: HTTP_INTERCEPTORS, useClass: XsrfInterceptor, multi: true },
        { provide: HTTP_INTERCEPTORS, useClass: LoadingInterceptor, multi: true },
        { provide: ErrorHandler, useClass: MEErrorHandler },
        { provide: RouteReuseStrategy, useClass: IonicRouteStrategy },
        LoadingService,
        MEErrorService,
        MEAPIService,
        ExternalSettingsService,
        ResponsiveDesignService,
        { provide: ENVIRONMENT_INITIALIZER, multi: true, useValue() { inject(HelpDeskService); } },
        CurrencyPipe,
        DecimalPipe,
        TitleCasePipe,
        IonicRouteStrategy,
        FingerprintAIO,
        provideLottieOptions({
            player: () => player,
        }),
        provideCacheableAnimationLoader(),
        provideHttpClient(withInterceptorsFromDi(), withXsrfConfiguration({
						"cookieName": "XSRF-TOKEN",
						"headerName": "X-XSRF-TOKEN"
					})),
    ] })
export class AppModule {
  constructor() { }
}

export function HttpLoaderFactory(http: HttpClient): TranslateHttpLoader {
  return new TranslateHttpLoader(http, './assets/i18n/');
}
